// client/src/components/common/AnalyticsListener.js
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { initAnalytics, trackPageView } from "../../utils/analytics";

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const IS_PROD = process.env.NODE_ENV === "production";

export default function AnalyticsListener() {
  const location = useLocation();

  // Initialize GA once; on mobile, only after first interaction to avoid unused JS
  useEffect(() => {
    if (!IS_PROD || !GA_ID) return;

    let initialized = false;

    const doInit = () => {
      if (initialized) return;
      initialized = true;
      initAnalytics(GA_ID);
      // First page view after init
      try {
        trackPageView(location.pathname + location.search);
      } catch {}
      cleanup();
    };

    // Detect mobile using a conservative viewport check
    const isMobile = typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 767px)').matches
      : (window.innerWidth || 1024) < 768;

    const onFirstInteraction = () => doInit();
    const onLoad = () => doInit();
    const cleanup = () => {
      window.removeEventListener("pointerdown", onFirstInteraction);
      window.removeEventListener("keydown", onFirstInteraction);
      window.removeEventListener("load", onLoad);
    };

    // Scheduling policy:
    // - Mobile: initialize only on first interaction (no idle fallback)
    // - Desktop: allow load or idle after ~3s
    window.addEventListener("pointerdown", onFirstInteraction, { once: true });
    window.addEventListener("keydown", onFirstInteraction, { once: true });
    if (!isMobile) {
      if ('requestIdleCallback' in window) {
        // @ts-ignore
        window.requestIdleCallback(() => doInit(), { timeout: 3000 });
      } else {
        setTimeout(doInit, 3000);
      }
      window.addEventListener("load", onLoad, { once: true });
    }

    return cleanup;
  }, [location.pathname, location.search]);

  // Track page views on route change
  useEffect(() => {
    if (IS_PROD && GA_ID) {
      // Will no-op until initAnalytics has run
      trackPageView(location.pathname + location.search);
    }
  }, [location]);

  return null;
}
